import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react'
import posthog from 'posthog-js'
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Box } from '@mui/material'
import { config } from '../config/environment'

/**
 * PostHogProvider - Feature flags and product analytics via PostHog
 *
 * This provider handles:
 * - PostHog initialization with API key from environment config
 * - Feature flag checking via useFeatureFlag hook
 * - Event tracking via trackEvent function
 *
 * Config values (from environment.ts, generated at build time):
 * - ANALYTICS_API_KEY: Analytics API key (PostHog project key)
 * - ANALYTICS_HOST: Analytics host (defaults to https://us.i.posthog.com)
 *
 * NOTE: User identification should be done separately using identifyUser()
 * after user identity is available (e.g., after login/registration).
 * This keeps PostHogProvider independent of UserIdentityContext.
 */

interface PostHogContextType {
  isInitialized: boolean
  areFlagsLoaded: boolean
  isFeatureEnabled: (flag: string) => boolean
  trackEvent: (event: string, properties?: Record<string, any>) => void
  identifyUser: (email: string, properties?: { department?: string; userId?: string }) => void
}

const PostHogContext = createContext<PostHogContextType | undefined>(undefined)

interface PostHogProviderProps {
  children: ReactNode
}

export function PostHogProvider({ children }: PostHogProviderProps): JSX.Element {
  const [isInitialized, setIsInitialized] = useState(false)
  const [areFlagsLoaded, setAreFlagsLoaded] = useState(false)
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({})
  // Dev-only: surface a missing ANALYTICS_API_KEY as a dismissible modal so it's
  // not silently swallowed (flag-gated UI just vanishes otherwise). Never shown
  // outside local development.
  const [showKeyMissingModal, setShowKeyMissingModal] = useState(false)

  // Initialize PostHog
  useEffect(() => {
    const apiKey = config.ANALYTICS_API_KEY
    const host = config.ANALYTICS_HOST || 'https://us.i.posthog.com'

    if (!apiKey) {
      console.warn(
        '[PostHog] ANALYTICS_API_KEY is not set — feature flags are DISABLED ' +
        '(flag-gated UI will be hidden, with no other error).\n' +
        '  Local dev: this key is injected into src/config/environment.ts at deploy ' +
        'from SSM, but is intentionally blank in the committed file. To enable flags ' +
        'locally, set the PostHog project key — see reach3insight-app/.env.example ' +
        '(ANALYTICS_API_KEY). Ask the team for the value; do NOT commit it.',
      )
      // Dev-only modal so the missing key is impossible to miss. Not in deployed envs.
      if (config.ENVIRONMENT === 'development') setShowKeyMissingModal(true)
      setIsInitialized(true) // Mark as initialized even without key (graceful degradation)
      setAreFlagsLoaded(true) // No flags to load
      return
    }

    try {
      posthog.init(apiKey, {
        api_host: host,
        // Required: capture person profiles for all users (not just identified)
        // This ensures feature flags work before and after identify() is called
        person_profiles: 'always',
        loaded: () => {
          console.log('[PostHog] Initialized successfully')
          setIsInitialized(true)
        }
      })

      // Listen for feature flag updates
      posthog.onFeatureFlags((flags) => {
        const flagState: Record<string, boolean> = {}
        flags.forEach((flag) => {
          flagState[flag] = posthog.isFeatureEnabled(flag) ?? false
        })
        setFeatureFlags(flagState)
        setAreFlagsLoaded(true)
        console.log('[PostHog] Feature flags loaded:', flags)
      })
    } catch (error) {
      console.error('[PostHog] Initialization failed:', error)
      setIsInitialized(true) // Still mark as initialized for graceful degradation
      setAreFlagsLoaded(true) // Assume no flags on error
    }

    return () => {
      // Cleanup on unmount (optional - PostHog handles this)
    }
  }, [])

  const isFeatureEnabled = useCallback((flag: string): boolean => {
    // If analytics not configured, default to false (feature disabled)
    if (!config.ANALYTICS_API_KEY) {
      return false
    }

    // Check cached flags first, then PostHog directly
    if (flag in featureFlags) {
      return featureFlags[flag]
    }

    return posthog.isFeatureEnabled(flag) ?? false
  }, [featureFlags])

  const trackEvent = useCallback((event: string, properties?: Record<string, any>): void => {
    if (!config.ANALYTICS_API_KEY) {
      return // Silently skip if not configured
    }

    posthog.capture(event, properties)
  }, [])

  /**
   * Identify user for feature flag targeting
   * Call this after user logs in or registers
   *
   * @param email - User's email (used as distinct_id in PostHog)
   * @param properties - Optional user properties for targeting (department, userId)
   */
  const identifyUser = useCallback((email: string, properties?: { department?: string; userId?: string }): void => {
    if (!config.ANALYTICS_API_KEY) {
      return // Silently skip if not configured
    }

    posthog.identify(email, {
      email: email,
      department: properties?.department,
      userId: properties?.userId
    })
    // Reload feature flags after identifying to get user-specific flags
    posthog.reloadFeatureFlags()
    console.log('[PostHog] User identified:', email)
  }, [])

  const value: PostHogContextType = {
    isInitialized,
    areFlagsLoaded,
    isFeatureEnabled,
    trackEvent,
    identifyUser
  }

  return (
    <PostHogContext.Provider value={value}>
      {children}
      <PostHogKeyMissingDialog
        open={showKeyMissingModal}
        onClose={() => setShowKeyMissingModal(false)}
      />
    </PostHogContext.Provider>
  )
}

/**
 * Dev-only modal shown when ANALYTICS_API_KEY is missing. The key is injected
 * into environment.ts from SSM at deploy, but is intentionally blank in the
 * committed file — so locally, flag-gated UI just vanishes with no error. This
 * makes that state impossible to miss. Never rendered in deployed environments
 * (gated on config.ENVIRONMENT === 'development' at the call site).
 */
function PostHogKeyMissingDialog({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const codeSx = { px: 0.5, py: 0.25, bgcolor: 'action.hover', borderRadius: 0.5, fontFamily: 'monospace' }
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Feature flags are disabled (local dev)</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <p style={{ marginTop: 0 }}>
            <strong>ANALYTICS_API_KEY</strong> is not set, so PostHog feature flags
            are off. Any flag-gated UI will be hidden — with no other error. If
            something you expect to see is missing, this is why.
          </p>
          <p style={{ marginBottom: 0 }}>
            To enable flags locally, set the PostHog project key in{' '}
            <Box component="code" sx={codeSx}>packages/shared/src/config/environment.ts</Box>{' '}
            (<strong>ANALYTICS_API_KEY</strong>) for your local run only — see{' '}
            <Box component="code" sx={codeSx}>.env.example</Box>. Ask the team for
            the value, and <strong>do not commit it</strong>.
          </p>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Got it</Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * Hook to access PostHog context
 */
export function usePostHog(): PostHogContextType {
  const context = useContext(PostHogContext)

  if (context === undefined) {
    throw new Error('usePostHog must be used within a PostHogProvider')
  }

  return context
}

/**
 * Convenience hook for checking a single feature flag
 *
 * @param flag - Feature flag name (e.g., 'visual-semiotics-enabled')
 * @returns boolean indicating if feature is enabled
 *
 * @example
 * const isVisualSemioticsEnabled = useFeatureFlag('visual-semiotics-enabled')
 */
export function useFeatureFlag(flag: string): boolean {
  const { isFeatureEnabled } = usePostHog()
  return isFeatureEnabled(flag)
}

export { PostHogContext }
